import { useState, useRef, useEffect } from 'react';
import { Conversation } from '../hooks/useConversations';
import { ClarifyingQuestions } from '../../../components/ClarifyingQuestions';
import '../../../components/ClarifyingQuestions.css';
import { getApiUrl } from '../utils/api';
import { getModelId } from '../../../components/ModelSelector';

export type ExportMode = 'merged_stl' | 'assembly_step' | 'individual_parts';

// Types for clarifying questions
interface QuestionOption {
  value: string;
  label: string;
  params: Record<string, any>;
  suggested?: boolean;
}

interface Question {
  id: string;
  question: string;
  type: 'select' | 'multiselect';
  options: QuestionOption[];
}

interface ClarificationData {
  needs_clarification: boolean;
  object_type: string | null;
  questions: Question[];
  defaults: Record<string, any>;
  message: string;
  original_prompt: string;
}

interface MessageDisplay {
  id: string;
  sender: 'user' | 'ai';
  content: string;
  timestamp: Date;
  images?: string[];
  codePreview?: string;
}

interface ConversationPanelProps {
  conversations: Conversation[];
  currentConversationId: number | null;
  onNewChat: () => void;
  onSwitchConversation: (id: number) => void;
  onSendMessage: (message: string, images?: string[], clarificationAnswers?: Record<string, any>, objectType?: string, useDefaults?: boolean, model?: string) => void;
  onDownload: (mode: ExportMode) => void;
  showDownloadButton: boolean;
  formatTime: (date: Date) => string;
  messages?: MessageDisplay[];
  isLoading?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const ConversationPanel: React.FC<ConversationPanelProps> = ({
  conversations,
  currentConversationId,
  onNewChat,
  onSwitchConversation: _onSwitchConversation,
  onSendMessage,
  onDownload,
  showDownloadButton,
  formatTime: _formatTime,
  messages: externalMessages,
  isLoading = false,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [message, setMessage] = useState('');
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [clarificationData, setClarificationData] = useState<ClarificationData | null>(null);
  const [pendingImages, setPendingImages] = useState<string[] | undefined>(undefined);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const imageUploadContainerRef = useRef<HTMLDivElement>(null);
  const imageHandlerRef = useRef<any>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const currentConversation = conversations.find(c => c.id === currentConversationId);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentConversation?.messages]);

  // Auto-resize textarea when message changes
  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'inherit';
      chatInputRef.current.style.height = '1px';
      const contentHeight = chatInputRef.current.scrollHeight;
      chatInputRef.current.style.height = Math.max(24, contentHeight) + 'px';
    }
  }, [message]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Initialize ImageUploadHandler if available
    if (typeof (window as any).ImageUploadHandler !== 'undefined' && imageUploadContainerRef.current) {
      // Create unique container ID for this instance
      const containerId = `imageUploadContainer_${Date.now()}`;
      
      imageHandlerRef.current = new (window as any).ImageUploadHandler({
        maxFileSize: 5 * 1024 * 1024,
        allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'],
        onImageAdded: (img: { base64: string; id: string }) => {
          setUploadedImages(prev => [...prev, img.base64]);
        },
        onImageRemoved: (_imgId: string, _removedImg: any) => {
          // Find which image was removed based on the handler's current state
          if (imageHandlerRef.current) {
            const currentImages = imageHandlerRef.current.getImagesForAPI();
            setUploadedImages(currentImages);
          }
        }
      });

      // Create container element
      const container = document.createElement('div');
      container.id = containerId;
      imageUploadContainerRef.current.appendChild(container);
      
      // Initialize with React button mode (no duplicate button)
      imageHandlerRef.current.init(containerId, { showButton: false });
    }

    return () => {
      if (imageHandlerRef.current) {
        // Use destroy method if available
        if (imageHandlerRef.current.destroy) {
          imageHandlerRef.current.destroy();
        }
        imageHandlerRef.current = null;
      }
      if (imageUploadContainerRef.current) {
        imageUploadContainerRef.current.innerHTML = '';
      }
    };
  }, []);

  const getImagesForSubmit = (): string[] | undefined => {
    let imagesForAPI: string[] | undefined = undefined;
    if (imageHandlerRef.current && imageHandlerRef.current.images && imageHandlerRef.current.images.length > 0) {
      try {
        imagesForAPI = imageHandlerRef.current.getImagesForAPI();
        if (imagesForAPI) {
          imagesForAPI = imagesForAPI.filter(img => {
            if (!img || typeof img !== 'string') return false;
            if (!img.startsWith('data:image')) return false;
            const parts = img.split(',');
            return parts.length === 2 && !!parts[1];
          });
          if (imagesForAPI.length === 0) imagesForAPI = undefined;
        }
      } catch (error) {
        console.error('Error getting images from handler:', error);
        imagesForAPI = undefined;
      }
    } else if (uploadedImages.length > 0) {
      imagesForAPI = uploadedImages
        .filter(img => img && typeof img === 'string' && img.startsWith('data:image') && img.includes(','));
      if (imagesForAPI.length === 0) imagesForAPI = undefined;
    }
    return imagesForAPI;
  };

  const clearInputs = () => {
    setMessage('');
    setUploadedImages([]);
    if (imageHandlerRef.current && imageHandlerRef.current.clear) {
      imageHandlerRef.current.clear();
    }
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'auto';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() && uploadedImages.length === 0) return;

    const imagesForAPI = getImagesForSubmit();
    const currentMessage = message;

    // Analyze prompt for clarifying questions
    setIsAnalyzing(true);
    try {
      const response = await fetch(getApiUrl('/api/analyze-prompt'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: currentMessage })
      });
      
      const data = await response.json();
      
      if (data.success && data.needs_clarification && data.questions && data.questions.length > 0) {
        // Show clarifying questions
        setClarificationData({
          needs_clarification: true,
          object_type: data.object_type,
          questions: data.questions,
          defaults: data.defaults || {},
          message: data.message || `A few quick questions to create your ${data.object_type || 'design'}:`,
          original_prompt: currentMessage
        });
        setPendingImages(imagesForAPI);
        // Don't clear inputs yet - user might want to edit
      } else {
        // No clarification needed - proceed directly
        onSendMessage(currentMessage, imagesForAPI, undefined, undefined, undefined, getModelId());
        clearInputs();
        setClarificationData(null);
        setPendingImages(undefined);
      }
    } catch (error) {
      console.error('Error analyzing prompt:', error);
      // On error, proceed without clarification
      onSendMessage(currentMessage, imagesForAPI, undefined, undefined, undefined, getModelId());
      clearInputs();
      setClarificationData(null);
      setPendingImages(undefined);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleClarificationSubmit = (answers: Record<string, any>) => {
    if (!clarificationData) return;
    
    onSendMessage(
      clarificationData.original_prompt, 
      pendingImages, 
      answers, 
      clarificationData.object_type || undefined,
      false,
      getModelId()
    );
    
    clearInputs();
    setClarificationData(null);
    setPendingImages(undefined);
  };

  const handleClarificationSkip = () => {
    if (!clarificationData) return;
    
    onSendMessage(
      clarificationData.original_prompt, 
      pendingImages, 
      undefined, 
      clarificationData.object_type || undefined,
      true, // use defaults
      getModelId()
    );
    
    clearInputs();
    setClarificationData(null);
    setPendingImages(undefined);
  };

  const autoResize = (textarea: HTMLTextAreaElement) => {
    // Reset height to minimum to get accurate scrollHeight measurement
    textarea.style.height = 'inherit';
    textarea.style.height = '1px';
    
    // Get the actual content height needed
    const contentHeight = textarea.scrollHeight;
    
    // Set height to fit all content (minimum 24px)
    textarea.style.height = Math.max(24, contentHeight) + 'px';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (isCollapsed) {
    return null; // When collapsed, don't render the panel
  }

  return (
    <div className="conversation-panel">
      <div className="sidebar-header">
        <div className="sidebar-header-actions">
          <button className="new-chat-button" onClick={onNewChat}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 3.5V9M9 9V14.5M9 9H3.5M9 9H14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span>New conversation</span>
          </button>
          {/* Collapse button */}
          {onToggleCollapse && (
            <button 
              className="collapse-sidebar-btn"
              onClick={onToggleCollapse}
              title="Hide chat panel"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="11 17 6 12 11 7"></polyline>
                <polyline points="18 17 13 12 18 7"></polyline>
              </svg>
            </button>
          )}
          {showDownloadButton && (
            <div className="download-dropdown" ref={exportMenuRef}>
              <button 
                className="download-button" 
                onClick={() => setShowExportMenu(!showExportMenu)}
              >
                <svg className="download-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M3 12V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span>Export</span>
                <svg className="dropdown-arrow" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 12, height: 12, marginLeft: 4 }}>
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button 
                    className="export-option"
                    onClick={() => { onDownload('merged_stl'); setShowExportMenu(false); }}
                  >
                    <span className="export-option-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg></span>
                    <div className="export-option-text">
                      <span className="export-option-title">Merged STL</span>
                      <span className="export-option-desc">Single file for 3D printing</span>
                    </div>
                  </button>
                  <button 
                    className="export-option"
                    onClick={() => { onDownload('assembly_step'); setShowExportMenu(false); }}
                  >
                    <span className="export-option-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg></span>
                    <div className="export-option-text">
                      <span className="export-option-title">Assembly STEP</span>
                      <span className="export-option-desc">Editable parts for CAD software</span>
                    </div>
                  </button>
                  <button 
                    className="export-option"
                    onClick={() => { onDownload('individual_parts'); setShowExportMenu(false); }}
                  >
                    <span className="export-option-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg></span>
                    <div className="export-option-text">
                      <span className="export-option-title">Individual Parts</span>
                      <span className="export-option-desc">Separate file per component (ZIP)</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="chat-messages" id="chatMessages">
        {/* Use external messages if provided, otherwise use conversation messages */}
        {(externalMessages || currentConversation?.messages)?.map((msg, idx) => {
          const msgDisplay = msg as MessageDisplay;
          const hasImages = msgDisplay.images && msgDisplay.images.length > 0;
          const hasCodePreview = msgDisplay.codePreview && msgDisplay.codePreview.length > 0;
          
          return (
            <div key={msgDisplay.id || idx} className={`message ${msg.sender}`}>
              {/* Show images if present */}
              {hasImages && (
                <div className="message-images">
                  {msgDisplay.images!.map((img, imgIdx) => (
                    <img 
                      key={imgIdx} 
                      src={img.startsWith('data:') ? img : `${img}`} 
                      alt={`Uploaded ${imgIdx + 1}`}
                      className="message-image"
                    />
                  ))}
                </div>
              )}
              {/* Show text content */}
              {msg.content && <div className="message-content">{msg.content}</div>}
              {/* Show collapsible code preview like thinking traces */}
              {hasCodePreview && (
                <details className="code-preview-details">
                  <summary className="code-preview-summary">
                    <span className="code-icon">📄</span> View generated code
                  </summary>
                  <pre className="code-preview-content">{msgDisplay.codePreview}</pre>
                </details>
              )}
          </div>
          );
        })}
        
        {/* Show loading indicator */}
        {isLoading && (
          <div className="message ai">
            <div className="message-content analyzing">
              <span className="analyzing-dot">●</span>
              <span className="analyzing-dot">●</span>
              <span className="analyzing-dot">●</span>
              <span style={{ marginLeft: 8 }}>Generating your model...</span>
            </div>
          </div>
        )}
        
        {/* Show analyzing indicator */}
        {isAnalyzing && !isLoading && (
          <div className="message ai">
            <div className="message-content analyzing">
              <span className="analyzing-dot">●</span>
              <span className="analyzing-dot">●</span>
              <span className="analyzing-dot">●</span>
              <span style={{ marginLeft: 8 }}>Analyzing your request...</span>
            </div>
          </div>
        )}
        
        {/* Show clarifying questions if needed */}
        {clarificationData && clarificationData.needs_clarification && (
          <ClarifyingQuestions
            objectType={clarificationData.object_type}
            questions={clarificationData.questions}
            defaults={clarificationData.defaults}
            message={clarificationData.message}
            onSubmit={handleClarificationSubmit}
            onSkip={handleClarificationSkip}
          />
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <div className="chat-input-container">
          <div ref={imageUploadContainerRef} id="imageUploadContainer"></div>
          <div className="chat-input-wrapper">
            <textarea
              ref={chatInputRef}
              className="chat-input"
              placeholder="Describe what to create, or upload an image..."
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                autoResize(e.target);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="input-actions">
            <button
              type="button"
              className="attach-button"
              title="Add Image"
              onClick={() => {
                // Trigger image upload via ImageUploadHandler if available
                if (imageHandlerRef.current) {
                  // Use the new openFilePicker method
                  if (imageHandlerRef.current.openFilePicker) {
                    imageHandlerRef.current.openFilePicker();
                  } else {
                    // Fallback for older handler version
                    const fileInput = imageUploadContainerRef.current?.querySelector('input[type="file"]') as HTMLInputElement;
                    if (fileInput) {
                      fileInput.click();
                    }
                  }
                } else {
                  // Fallback: create a temporary file input and handle files manually
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.multiple = true;
                  input.onchange = async (e: any) => {
                    const files = e.target.files;
                    if (files && files.length > 0) {
                      // Process files manually if no handler
                      for (const file of files) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const base64 = event.target?.result as string;
                          if (base64 && base64.startsWith('data:image')) {
                            setUploadedImages(prev => [...prev, base64]);
                          }
                        };
                        reader.readAsDataURL(file);
                      }
                    }
                  };
                  input.click();
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 10V12C14 13.1046 13.1046 14 12 14H4C2.89543 14 2 13.1046 2 12V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M8 2V10M8 2L5 5M8 2L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              className="send-button"
              onClick={handleSubmit}
              disabled={!message.trim() && uploadedImages.length === 0 && !(imageHandlerRef.current?.hasImages?.())}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 2L8 14M8 2L4 6M8 2L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Declare ImageUploadHandler globally
declare global {
  interface Window {
    ImageUploadHandler?: any;
  }
}

